import { mergeCoplanarFaces } from "building-generation/faceMerge";
import type { RoofFace, RoofFaceProps } from "building-types/roofs/roofFace";
import type { Table, RWTable } from "common/data/table";
import hashString from "common/utils/hashString";
import type { Face } from "geom/brep";

const roofFacePropsToIncludeInHash: { [key in keyof RoofFaceProps]: boolean } = {
  type: true,
  side: true,
  isDormer: true,
  annotationSettings: false,
  disableOverhangClipping: true,
};

function getRoofFaceHash(face: RoofFace): string {
  let json: string = "";
  for (const key in roofFacePropsToIncludeInHash) {
    if (roofFacePropsToIncludeInHash[key]) {
      json += JSON.stringify(face[key]);
    }
  }

  return hashString(json).toString();
}

interface RoofFaceGroup extends RoofFaceProps {
  faces: Face[];
}

function groupRoofTopFaces(roofFaces: RoofFace[]): Table<RoofFaceGroup> {
  const groups: RWTable<RoofFaceGroup> = {};
  for (const roofFace of roofFaces) {
    const hash = getRoofFaceHash(roofFace);
    if (!groups[hash]) {
      groups[hash] = {
        ...roofFace,
        faces: [],
      };
    }

    groups[hash].faces.push(roofFace.face);
  }

  return groups;
}

export function mergeCoplanarRoofTopFaces(faces: RoofFace[]): RoofFace[] {
  // create groups of faces with the same properties
  const groups = groupRoofTopFaces(faces);

  // merge faces in each group
  const mergedGroups: RWTable<RoofFaceGroup> = {};
  for (const key in groups) {
    const group = groups[key];

    const mergedFaces = mergeCoplanarFaces({ inputFaces: group.faces });

    mergedGroups[key] = {
      ...group,
      faces: mergedFaces,
    };
  }

  // create new faces from merged groups
  const mergedFaces: RoofFace[] = [];
  for (const key in mergedGroups) {
    const { faces, ...props } = mergedGroups[key];
    for (const face of faces) {
      mergedFaces.push({
        ...props,
        face,
      });
    }
  }

  return mergedFaces;
}
